import { SubscriptionDiscount } from "__generated__/globalTypes";
import { SUBSCRIPTION_NAME_LOWER } from "constants/site";

export type DiscountCalculationsFromSubscription = {
  isFree: boolean;
  isDiscounted: boolean;
  isFixedPrice: boolean;
  hasNewPrice: boolean;
  isFreeOrDiscounted: boolean;
  discountPercent: number | null;
  discountedPrice: number | null;
} & Pick<SubscriptionDiscount, "usageCount" | "usageLimit" | "ceCount" | "ceLimit">;

interface ProductWithActiveDiscount {
  priceUSD: number;
  userDiscountFromSubscription?: SubscriptionDiscount | null;
}

export const useDiscountCalculationsFromSubscription = (
  product: ProductWithActiveDiscount
): DiscountCalculationsFromSubscription => {
  const userDiscount = product.userDiscountFromSubscription;

  const isFree = userDiscount?.percent === 100;
  const isDiscounted = !isFree && (userDiscount?.percent || 0) > 0;
  const isFixedPrice = !!userDiscount?.fixedPrice;

  const discountPercent = (isDiscounted ? userDiscount?.percent : null) || null;
  const discountedPrice =
    (discountPercent ? product.priceUSD * (1 - discountPercent / 100) : userDiscount?.fixedPrice) ||
    null;

  const hasNewPrice = isDiscounted || isFixedPrice;

  const usageCount = userDiscount?.usageCount || 0;
  const usageLimit = userDiscount?.usageLimit || 0;
  const ceCount = userDiscount?.ceCount || 0;
  const ceLimit = userDiscount?.ceLimit || 0;

  return {
    isFree,
    isDiscounted,
    isFixedPrice,
    hasNewPrice,
    isFreeOrDiscounted: isFree || hasNewPrice,
    discountPercent,
    discountedPrice,
    usageCount,
    usageLimit,
    ceCount,
    ceLimit,
  };
};

export type DiscountOptionsFromSubscription = DiscountCalculationsFromSubscription & {
  activeDiscountFromSubscription?: SubscriptionDiscount;
  discountOptions: { value: string; label: string }[] | null;
};

interface Product {
  priceUSD: number;
  userDiscountsFromSubscription?: SubscriptionDiscount[] | null;
}

export const useDiscountOptionsFromSubscription = (
  product: Product,
  discountId?: string
): DiscountOptionsFromSubscription => {
  const discount =
    discountId === undefined
      ? product.userDiscountsFromSubscription?.[0]
      : product.userDiscountsFromSubscription?.find(d => d.id === discountId);

  const details = useDiscountCalculationsFromSubscription({
    priceUSD: product.priceUSD,
    userDiscountFromSubscription: discount,
  });

  const discountOptions = product.userDiscountsFromSubscription?.length
    ? product.userDiscountsFromSubscription
        ?.map(d => ({
          value: d.id,
          label: d.displayName || "Unknown discount",
        }))
        .concat({ value: "", label: `Pay without ${SUBSCRIPTION_NAME_LOWER}` })
    : null;

  return {
    ...details,
    discountOptions,
    activeDiscountFromSubscription: discount,
  };
};
